import { Link } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { proxyImage } from "@/lib/imageProxy";
import { PrefetchLink } from "@/components/PrefetchLink";
import { Badge } from "@/components/ui/badge";
import { Heart, Users, ArrowRight, Loader2, AlertCircle, RefreshCw, MapPin } from "lucide-react";
import { useInstitutionsWithStats } from "@/hooks/useInstitutions";
import { useTranslation } from "react-i18next";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export const CausesShowcase = () => {
  const { t } = useTranslation("public");
  const { data: institutions, isLoading, error, refetch } = useInstitutionsWithStats(8);

  return (
    <section className="py-16 md:py-24 bg-secondary/30">
      <div className="container">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">
              {t("causesShowcase.title")}
            </h2>
            <p className="text-muted-foreground max-w-xl">
              {t("causesShowcase.subtitle")}
            </p>
          </div>
            <Button variant="outline" asChild>
              <PrefetchLink to="/causas" className="gap-2">
                {t("causesShowcase.viewAll")}
                <ArrowRight className="h-4 w-4" />
              </PrefetchLink>
            </Button>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">{t("causesShowcase.loading")}</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <AlertCircle className="h-10 w-10 text-destructive" />
            <p className="text-muted-foreground text-center">
              {t("causesShowcase.error")}
            </p>
            <Button variant="outline" onClick={() => refetch()} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              {t("causesShowcase.retry")}
            </Button>
          </div>
        ) : !institutions || institutions.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">
              {t("causesShowcase.empty")}
            </p>
        ) : (
          <div className="px-8 md:px-12">
            <Carousel
              opts={{
                align: "start",
                loop: false,
              }}
              className="w-full"
            >
              <CarouselContent className="-ml-4">
                {institutions.map((institution) => (
                  <CarouselItem
                    key={institution.id}
                    className="pl-4 basis-full sm:basis-1/2 lg:basis-1/4"
                  >
                    <CauseCard institution={institution} />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="-left-10 md:-left-12" />
              <CarouselNext className="-right-10 md:-right-12" />
            </Carousel>
          </div>
        )}
      </div>
    </section>
  );
};

interface CauseCardProps {
  institution: {
    id: string;
    name: string;
    description: string | null;
    logo_url: string | null;
    slug: string | null;
    total_donations: number | null;
    supporters_count: number;
    cover_url?: string | null;
    city?: string | null;
    state?: string | null;
    category?: string | null;
  };
}

const CauseCard = ({ institution }: CauseCardProps) => {
  const { t } = useTranslation("public");
  const location = [institution.city, institution.state].filter(Boolean).join(", ");

  return (
    <PrefetchLink
      to={`/causa/${institution.slug || institution.id}`}
      className="block h-full"
    >
      <Card className="group hover:shadow-lg transition-all duration-300 cursor-pointer h-full overflow-hidden border-0 shadow-md">
        {/* Cover Image */}
        <div className="relative h-40 overflow-hidden">
          {institution.cover_url ? (
            <img
              src={proxyImage(institution.cover_url, { w: 480 }) ?? institution.cover_url}
              alt={t("causesShowcase.coverAlt", { name: institution.name })}
              width={480}
              height={160}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"

              loading="lazy"
              decoding="async"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
                (e.target as HTMLImageElement).nextElementSibling?.classList.remove("hidden");
              }}
            />
          ) : null}
          <div
            className={`${institution.cover_url ? "hidden" : ""} absolute inset-0 bg-gradient-to-br from-primary/60 via-primary/40 to-accent/50`}
          />

          {/* Category Badge */}
          {institution.category && (
            <Badge className="absolute top-3 left-3 bg-hsl(var(--success)) text-success-foreground border-0 shadow-xs px-3 py-1 text-xs font-semibold"
              style={{ backgroundColor: 'hsl(var(--success))', color: 'white' }}
            >
              {institution.category}
            </Badge>
          )}
        </div>

        <CardContent className="p-5 pt-0 relative">
          {/* Logo overlapping cover */}
          <div className="-mt-8 mb-3 relative z-10">
            {institution.logo_url ? (
              <img
                src={proxyImage(institution.logo_url, { w: 128 }) ?? institution.logo_url}
                alt={institution.name}
                width={64}
                height={64}
                className="h-16 w-16 rounded-full object-cover border-4 border-card bg-card shadow-md"

                loading="lazy"
                decoding="async"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "/placeholder.svg";
                }}
              />
            ) : (
              <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center border-4 border-card shadow-md">
                <Heart className="h-7 w-7 text-primary" />
              </div>
            )}
          </div>

          {/* Name */}
          <h3 className="font-display font-bold text-base mb-1 group-hover:text-primary transition-colors line-clamp-2 min-h-[2.75rem]">
            {institution.name}
          </h3>

          {/* Location */}
          {location && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate">{location}</span>
            </div>
          )}

          {/* Description */}
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2 min-h-[2.5rem]">
            {institution.description || t("causesShowcase.defaultDescription")}
          </p>

          {/* Metrics */}
          <div className="flex items-center justify-between py-3 border-t border-border/60 mb-3">
            <div className="flex items-center gap-1.5 text-sm">
              <Heart className="h-4 w-4" style={{ color: 'hsl(var(--success))' }} fill="currentColor" />
              <span className="font-bold" style={{ color: 'hsl(var(--success))' }}>
                {formatCurrency(institution.total_donations || 0)}
              </span>
            </div>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Users className="h-4 w-4" />
              <span className="font-medium">{institution.supporters_count || 0}</span>
            </div>
          </div>

          {/* CTA Button */}
          <Button
            variant="outline"
            className="w-full border-2 font-semibold transition-colors border-primary text-link hover:bg-primary hover:text-primary-foreground hover:border-primary"
            asChild
          >
            <span>{t("causesShowcase.viewCause")}</span>
          </Button>
        </CardContent>
      </Card>
    </PrefetchLink>
  );
};
